/**
 * The world outside the house: one sky, one horizon, one treeline, and rain falling
 * through the space between them and the walls.
 *
 * The single decision everything here follows from is that **none of this is parented to
 * the camera**. A skybox is conventionally re-centred on the eye every frame, which is what
 * makes it read as infinitely far — and it is exactly wrong for a house with windows in it,
 * because a backdrop that moves with the player has no parallax against the window frame.
 * Walking past a window would slide the world along with it, which is the flat pane this
 * replaces, bought at a higher price. So the sphere is placed once, around the house, and
 * left there: the player moving between rooms genuinely changes where they stand relative
 * to it, and each window frames a different part of it. Nothing simulates the depth.
 *
 * The same idea makes the rain work. Two cylinders at different radii sweep across the
 * view at different rates as the player moves, because they are at different distances —
 * which is what a curtain of rain in front of another curtain of rain actually looks like,
 * and what a single scrolling plane can never look like however it is drawn.
 *
 * Everything is unlit `meshBasicMaterial`, as the storm pane was: a window has to stay the
 * brightest thing on its wall even with the room's lamp switched off, and unlit is both the
 * only way to guarantee that and the cheapest thing to draw.
 *
 * The rain is the one thing here a level can do without. Standing *in* the storm is not the
 * same experience as looking at it through glass, so an outdoor level gets a dry, moonlit
 * break in the same weather instead (design D9): same sky, same horizon, no shells — see
 * `RainShells`, which is a separate component precisely so that a dry level runs no loop.
 *
 * Per-frame cost is two float mutations on two textures indoors, and nothing at all out.
 * No state, no store, no physics.
 */

import { Instance, Instances } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo } from 'react';
import { BackSide, CanvasTexture, DoubleSide, RepeatWrapping, SRGBColorSpace } from 'three';
import { buildExterior, outsideTrees } from '../game/profile/buildExterior';
import type { BuiltLevel } from '../game/profile/buildGeometry';
import { getGradientMap } from './materials';
import { exteriorCanvas, GROUND_COLOR, nearWoodsCanvas, rainShellCanvas } from './textures';
import { useDisposable } from './useDisposable';

type Props = {
  built: BuiltLevel;
  /** Seeds the skyline, so one profile renders the same woods on every build. */
  seed: string;
};

/**
 * How tall each rain shell stands, as a multiple of its own radius. At 4x, the top and
 * bottom edges sit ~63° off the horizontal seen from the middle of the house — outside the
 * camera's 37.5° half-FOV, so neither edge can come into view through a tall window.
 * Scaling with the radius rather than fixing it is what keeps that true for the far shell
 * as well as the near one.
 */
const SHELL_HEIGHT_FACTOR = 4;

/**
 * World metres covered by one tile of the rain texture, on **both** shells.
 *
 * This is what task 3.4 is about, and getting it wrong inverts the illusion: with a fixed
 * `repeat`, the far shell's larger circumference stretches each drop, so distant rain
 * renders bigger than near rain and the storm turns inside out.
 */
const RAIN_TILE_METRES = 3.5;

/**
 * Fall speed in tiles per second — and because a tile is the same size in metres on both
 * shells, the same number means the same speed in metres per second on both.
 *
 * That is deliberate, and it is a small correction to what the design anticipated. Real
 * rain does not fall more slowly further away; it only *appears* to, because the same
 * metres per second subtend a smaller angle at distance. Since the shells already differ in
 * distance, giving them equal world speed produces that difference for free and produces it
 * correctly. The 10% the far shell gives up is a gust, not a depth cue — it exists so the
 * two curtains do not scroll in visible lockstep.
 */
const RAIN_FALL_SPEED = 0.42;
const FAR_SHELL_GUST = 0.9;

/**
 * The far curtain is thinner. With nothing outside fogged (the backdrop would be erased by
 * a fog that ends at 45 m), this is what stands in for distance haze on the rain itself.
 *
 * The near one is thinner than it was too. It now hangs a couple of metres outside the
 * glass rather than ten, and at that range a dense curtain stops being weather and becomes
 * a screen over the window — the drops want to be big and sparse, not many.
 */
const NEAR_SHELL_OPACITY = 0.62;
const FAR_SHELL_OPACITY = 0.5;

/**
 * How far the ground plane sits below the floor line.
 *
 * Not just clearance any more: the house now stands slightly *above* the ground outside it,
 * which is what a house with a foundation does, and it stops the floor and the ground
 * reading as one continuous surface that happens to change colour at the wall.
 *
 * This is below the floor slab (which spans -0.5 to 0), so there *is* a gap between the
 * underside of the house and the ground outside it. That is survivable only because
 * everything down there is black: the gap, the ground and the backdrop's lower half are all
 * the same colour, so an edge the player could otherwise see under has nothing to show. If
 * the ground ever takes a colour again, this needs a plinth under the footprint or the house
 * will look like it is hovering.
 */
export const GROUND_DROP = 1.5;

/**
 * The standing trees. Dark enough to sit with the painted woods behind them, but not the
 * near-black those are drawn in — these catch the sun, so they need somewhere to catch it.
 */
const FOLIAGE_COLOR = '#16241f';
const TRUNK_COLOR = '#1d1712';

/**
 * A spruce, as three stacked skirts of foliage rather than one cone.
 *
 * A single cone is a triangle, and reads as one from any angle — there is no silhouette to
 * a shape whose outline is three straight lines. Overlapping tiers give the outline the
 * steps a conifer actually has, and each tier is rotated off the one below so the seven
 * facets do not line up into a smooth funnel. It costs nothing: same geometry, same
 * `Instances`, three instances per tree instead of one.
 *
 * Fractions of the tree's own height (`at` is the tier's base, `height` its length) and of
 * its spread (`radius`), so every tree keeps its proportions whatever size it grew.
 */
const TIERS = [
  { at: 0.24, radius: 1, height: 0.42 },
  { at: 0.48, radius: 0.76, height: 0.36 },
  { at: 0.71, radius: 0.5, height: 0.29 },
];

/** Where the sky falls back to when no canvas can be drawn (tests, SSR). */
const FALLBACK_SKY = '#1b2432';

/**
 * One rain shell's texture. Each shell needs its **own** instance even though both draw the
 * same canvas: `offset` lives on the texture, so a single shared one — which is what the
 * storm pane deliberately used — cannot drive two scroll positions.
 */
function shellTexture(canvas: HTMLCanvasElement, radius: number, height: number): CanvasTexture {
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.repeat.set(
    Math.max(1, Math.round((2 * Math.PI * radius) / RAIN_TILE_METRES)),
    Math.max(1, Math.round(height / RAIN_TILE_METRES)),
  );
  return texture;
}

/**
 * The storm, and nothing else — its own component so that a dry level does not merely skip
 * two meshes but stops running the loop that drives them.
 *
 * Hooks cannot be called conditionally, so as long as the `useFrame` below lived in
 * `ExteriorWorld` an outdoor level was paying for a callback every frame to discover there
 * was no rain to move. Lifting it into a component that is only mounted when it rains is
 * what makes "no rain outdoors" (design D9) a saving rather than a branch.
 */
function RainShells({ built, shells }: { built: BuiltLevel; shells: readonly [number, number] }) {
  // Keyed on `built` rather than on the two radii: the compiler cannot prove a value
  // destructured off `fit` is never mutated once `fit` is also passed elsewhere, and it
  // refuses to compile the component rather than silently memoize something unsafe.
  //
  // useMemo, unusually for this codebase: these are GPU resources, not a computation. A
  // texture has to be *the same object* across renders or the per-frame offset below is
  // written to one that has been replaced.
  const rain = useMemo(() => {
    const canvas = rainShellCanvas();
    // The radii are re-derived rather than read off the prop for the memo's sake; they are
    // the same two numbers. Null only where a canvas cannot be drawn at all (tests, SSR) —
    // a level with no rain never mounts this.
    const radii = buildExterior(built).shells;
    if (!canvas || !radii) return null;
    return radii.map((radius) => shellTexture(canvas, radius, radius * SHELL_HEIGHT_FACTOR));
  }, [built]);
  useDisposable(rain);

  useFrame((_, delta) => {
    if (!rain) return;
    // The offset *increases*, and the sign is not obvious enough to leave unexplained —
    // the storm pane this replaces had it the other way and its rain fell upwards.
    //
    // The sampled coordinate is `v * repeat + offset`, so a fixed feature in the texture
    // sits at `v = (sampled - offset) / repeat`: raising the offset lowers the v it appears
    // at. On a cylinder v runs 0 at the bottom to 1 at the top, so a rising offset walks
    // every drop downward. Subtracting makes it rain into the sky.
    //
    // Wrapped by hand rather than left to climb: an offset growing forever loses float
    // precision, and the rain visibly judders after a long session.
    rain[0]!.offset.y = (rain[0]!.offset.y + delta * RAIN_FALL_SPEED) % 1;
    rain[1]!.offset.y = (rain[1]!.offset.y + delta * RAIN_FALL_SPEED * FAR_SHELL_GUST) % 1;
  });

  return (
    <>
      {rain?.map((texture, index) => {
        const radius = shells[index]!;
        return (
          <mesh
            key={`rain-shell-${index}`}
            // Explicit, because these all share a bounding-sphere centre: three.js's
            // back-to-front sort between concentric transparent meshes is arbitrary and
            // free to flip. Back to front: far rain, near woods, near rain.
            renderOrder={index === 0 ? 3 : 1}
          >
            <cylinderGeometry args={[radius, radius, radius * SHELL_HEIGHT_FACTOR, 32, 1, true]} />
            <meshBasicMaterial
              map={texture}
              transparent
              opacity={index === 0 ? NEAR_SHELL_OPACITY : FAR_SHELL_OPACITY}
              depthWrite={false}
              side={BackSide}
              fog={false}
            />
          </mesh>
        );
      })}
    </>
  );
}

export function ExteriorWorld({ built, seed }: Props) {
  const fit = buildExterior(built);
  const { center, backdropRadius, nearWoodsRadius, shells } = fit;
  const trees = outsideTrees(seed, fit);

  // useMemo, unusually for this codebase: these are GPU resources, not a computation. The
  // React Compiler would memoize the call, but a texture also has to be *the same object*
  // across renders or anything mutating it later writes to one that has been replaced.
  const backdrop = useMemo(() => {
    const canvas = exteriorCanvas(seed);
    if (!canvas) return null;
    const texture = new CanvasTexture(canvas);
    // Wrapping, at a repeat of exactly 1: the skyline goes round the sphere once, and
    // `treeline` has already joined the seam. Wrapping rather than clamping is what lets
    // the first and last columns filter into each other, so the join does not show as a
    // hairline. What must never happen is a repeat above 1 — that would put the same three
    // trees outside two windows facing different ways.
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.colorSpace = SRGBColorSpace;
    return texture;
  }, [seed]);
  useDisposable(backdrop);

  const nearWoods = useMemo(() => {
    const canvas = nearWoodsCanvas(seed);
    if (!canvas) return null;
    const texture = new CanvasTexture(canvas);
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.colorSpace = SRGBColorSpace;
    return texture;
  }, [seed]);
  useDisposable(nearWoods);

  return (
    <group position={[center.x, 0, center.z]}>
      {/* A full sphere, not a dome. At eye height with a sill below you, part of every
          window looks *down* — a hemisphere would show the scene background under the
          horizon. Its equator sits at floor level, so the horizon reads as being a little
          below eye height, which is what a distant horizon does. */}
      <mesh>
        <sphereGeometry args={[backdropRadius, 48, 32]} />
        <meshBasicMaterial
          map={backdrop}
          color={backdrop ? '#ffffff' : FALLBACK_SKY}
          side={BackSide}
          // The scene fog runs to `#141821` by 45 m, which would render this flat. The
          // atmosphere is painted into the canvas instead, where it is tunable without
          // changing how the far end of an interior room looks.
          fog={false}
        />
      </mesh>

      {/* The ground the rain lands on.

          Without it the shells are just cylinders standing in space: they run from well
          below the horizon to well above it, so a player looking down through a window
          watched rain continue falling *underneath* the ground it was supposedly landing
          on. Painting ground onto the backdrop could never fix that — the backdrop is 30 m
          away and the rain is 10, so the rain is simply in front of it.

          Being opaque is the whole point: it draws in the opaque pass, writes depth, and
          the transparent shells then depth-test against it and get clipped at the ground
          line for free. It sits a few centimetres below y=0, inside the floor slab, so it
          cannot z-fight with the floor the player is standing on — and being under an
          opaque floor, it is invisible from indoors. Its rim meets the backdrop sphere at
          the equator, which is where that sphere's painted horizon already is. */}
      <mesh position={[0, -GROUND_DROP, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[backdropRadius, 64]} />
        <meshBasicMaterial color={GROUND_COLOR} side={DoubleSide} fog={false} />
      </mesh>

      {/* A handful of trees with actual volume, standing on the ground a few metres beyond
          the walls.

          Everything else out here is a painted surface at a fixed distance, so it moves as
          one piece however far away it is. These do not: walking across a room slides them
          against each other and against the woods behind them, which is the cue that says
          there is space out there rather than a very good picture of space.

          Two draw calls however many there are — foliage and trunks are each one
          `Instances`, the pattern `RoomTrim` already uses for the house's mouldings. Toon
          rather than unlit, unlike the rest of the exterior, because these are close enough
          to want a lit side and a dark one; a flat black cone this near reads as a hole. */}
      {trees.length > 0 && (
        <>
          <Instances limit={trees.length * TIERS.length} range={trees.length * TIERS.length}>
            <coneGeometry args={[1, 1, 7]} />
            <meshToonMaterial color={FOLIAGE_COLOR} gradientMap={getGradientMap()} fog={false} />
            {trees.flatMap((tree, index) =>
              TIERS.map((tier, level) => (
                <Instance
                  key={`foliage-${index}-${level}`}
                  position={[
                    tree.x,
                    -GROUND_DROP + tree.height * (tier.at + tier.height / 2),
                    tree.z,
                  ]}
                  rotation={[tree.lean, tree.spin + level, tree.lean]}
                  scale={[
                    tree.spread * tier.radius,
                    tree.height * tier.height,
                    tree.spread * tier.radius,
                  ]}
                />
              )),
            )}
          </Instances>

          <Instances limit={trees.length} range={trees.length}>
            <cylinderGeometry args={[1, 1.25, 1, 6]} />
            <meshToonMaterial color={TRUNK_COLOR} gradientMap={getGradientMap()} fog={false} />
            {trees.map((tree, index) => (
              <Instance
                key={`trunk-${index}`}
                position={[tree.x, -GROUND_DROP + tree.height * 0.16, tree.z]}
                rotation={[tree.lean, 0, tree.lean]}
                scale={[tree.spread * 0.11, tree.height * 0.32, tree.spread * 0.11]}
              />
            ))}
          </Instances>
        </>
      )}

      {/* The near woods, on their own sphere inside the backdrop — between the two rain
          shells indoors, and out behind the house on the far bank outdoors, where there is
          no rain and the depth is spent differently (`buildExterior`). This is the one part
          of the forest with real parallax: everything else is painted on the backdrop at a
          single distance, so crossing a room sweeps this row through a visibly larger angle
          than the ridge behind it, and the wood separates into layers. */}
      {nearWoods && (
        <mesh renderOrder={2}>
          <sphereGeometry args={[nearWoodsRadius, 48, 32]} />
          <meshBasicMaterial
            map={nearWoods}
            transparent
            depthWrite={false}
            side={BackSide}
            fog={false}
          />
        </mesh>
      )}

      {/* Weather, on the levels that have any. Outdoors the night is dry (design D9) and
          this is not mounted at all — which is also what stops its `useFrame` from
          running, since the level with the most to render is the one that was paying for
          a per-frame callback with nothing to move. */}
      {shells && <RainShells built={built} shells={shells} />}
    </group>
  );
}
